/**
 * 函数注册表
 * 用于管理验证函数、同步函数和可见性函数，支持按表单UUID隔离
 */
class FunctionRegistry {
  constructor() {
    // 存储验证函数，按formUuid分组
    this.validationFunctions = new Map()
    // 存储同步函数，按formUuid分组
    this.syncFunctions = new Map()
    // 存储可见性函数，按formUuid分组
    this.visibilityFunctions = new Map()
  }

  /**
   * 注册函数
   * @param {string} formUuid - 表单UUID，用于隔离不同表单的函数
   * @param {string} id - 函数ID
   * @param {string} code - 函数代码
   * @param {string} type - 函数类型 ('validation', 'sync' 或 'visibility')
   * @param {Object} metadata - 函数元数据
   */
  registerFunction(formUuid, id, code, type, metadata) {
    console.log('[函数注册表] 开始注册规则函数', this)

    let functionMap
    if (type === 'validation') {
      functionMap = this.validationFunctions
    } else if (type === 'sync') {
      functionMap = this.syncFunctions
    } else if (type === 'visibility') {
      functionMap = this.visibilityFunctions
    } else {
      console.error(`[函数注册表] 未知的函数类型: ${type}`)
      return
    }

    // 确保formUuid对应的Map存在
    if (!functionMap.has(formUuid)) {
      functionMap.set(formUuid, new Map())
    }

    const formFunctions = functionMap.get(formUuid)
    formFunctions.set(id, {
      id,
      code,
      type,
      metadata
    })

    console.log(`[函数注册表] 注册${type === 'validation' ? '验证' : type === 'sync' ? '同步' : '可见性'}函数:`, { formUuid, id, code, type, metadata })
    console.log('[函数注册表] 完成注册规则函数', this)
  }

  /**
   * 执行函数
   * @param {string} formUuid - 表单UUID
   * @param {string} id - 函数ID
   * @param {Object} values - 表单值对象
   * @param {Object} context - 上下文对象，包含labels和options等信息
   * @returns {boolean|Object} 验证函数返回boolean，同步函数返回{executed, newValue, targetelement}，可见性函数返回boolean
   */
  execute(formUuid, id, values, context = {}) {
    // 先尝试验证函数
    const validationFormFunctions = this.validationFunctions.get(formUuid)
    if (validationFormFunctions) {
      const funcInfo = validationFormFunctions.get(id)
      if (funcInfo) {
        try {
          const body = funcInfo.code.match(/{([\s\S]*)}/)[1]
          console.log('[函数注册表] 规则执行 values', values, 'context', context)
          const func = new Function('values', 'context', body)
          console.log(`[函数注册表] 执行验证函数: ${formUuid} -> ${id}`)
          return func(values, context)
        } catch (error) {
          console.error(`[函数注册表] 执行验证函数失败: ${formUuid} -> ${id}`, error)
          return false
        }
      }
    }

    // 尝试同步函数
    const syncFormFunctions = this.syncFunctions.get(formUuid)
    if (syncFormFunctions) {
      const funcInfo = syncFormFunctions.get(id)
      if (funcInfo) {
        try {
          const body = funcInfo.code.match(/{([\s\S]*)}/)[1]
          const func = new Function('values', 'context', body)
          console.log(`[函数注册表] 执行同步函数: ${formUuid} -> ${id}`)
          return func(values, context)
        } catch (error) {
          console.error(`[函数注册表] 执行同步函数失败: ${formUuid} -> ${id}`, error)
          return { executed: false }
        }
      }
    }

    // 尝试可见性函数
    const visibilityFormFunctions = this.visibilityFunctions.get(formUuid)
    if (visibilityFormFunctions) {
      const funcInfo = visibilityFormFunctions.get(id)
      if (funcInfo) {
        try {
          const body = funcInfo.code.match(/{([\s\S]*)}/)[1]
          const func = new Function('values', 'context', body)
          console.log(`[函数注册表] 执行可见性函数: ${formUuid} -> ${id}`)
          return func(values, context)
        } catch (error) {
          console.error(`[函数注册表] 执行可见性函数失败: ${formUuid} -> ${id}`, error)
          return true // 默认可见
        }
      }
    }

    console.warn(`[函数注册表] 未找到函数: ${formUuid} -> ${id}`)
    return false
  }

  /**
   * 获取指定表单的所有验证函数
   * @param {string} formUuid - 表单UUID
   * @returns {Array} 验证函数列表
   */
  getValidationFunctions(formUuid) {
    const formFunctions = this.validationFunctions.get(formUuid)
    if (!formFunctions) return []

    return Array.from(formFunctions.entries()).map(([id, { code, metadata }]) => ({
      id,
      code,
      metadata
    }))
  }

  /**
   * 根据id删除对应的规则
   * @param {string} formUuid
   * @param {string} ruleId
   */
  deleteFunctionById(formUuid, ruleId) {
    // 尝试从验证函数中删除
    const validationFormFunctions = this.validationFunctions.get(formUuid)
    if (validationFormFunctions && validationFormFunctions.has(ruleId)) {
      validationFormFunctions.delete(ruleId)
      console.log(`[函数注册表] 已从表单 ${formUuid} 删除验证函数: ${ruleId}`)
    }

    // 尝试从同步函数中删除
    const syncFormFunctions = this.syncFunctions.get(formUuid)
    if (syncFormFunctions && syncFormFunctions.has(ruleId)) {
      syncFormFunctions.delete(ruleId)
      console.log(`[函数注册表] 已从表单 ${formUuid} 删除同步函数: ${ruleId}`)
    }

    // 尝试从可见性函数中删除
    const visibilityFormFunctions = this.visibilityFunctions.get(formUuid)
    if (visibilityFormFunctions && visibilityFormFunctions.has(ruleId)) {
      visibilityFormFunctions.delete(ruleId)
      console.log(`[函数注册表] 已从表单 ${formUuid} 删除可见性函数: ${ruleId}`)
    }
  }

  /**
   * 获取指定表单的所有同步函数
   * @param {string} formUuid - 表单UUID
   * @returns {Array} 同步函数列表
   */
  getSyncFunctions(formUuid) {
    const formFunctions = this.syncFunctions.get(formUuid)
    if (!formFunctions) return []

    return Array.from(formFunctions.entries()).map(([id, { code, metadata }]) => ({
      id,
      code,
      metadata
    }))
  }

  /**
   * 获取指定表单的所有可见性函数
   * @param {string} formUuid - 表单UUID
   * @returns {Array} 可见性函数列表
   */
  getVisibilityFunctions(formUuid) {
    const formFunctions = this.visibilityFunctions.get(formUuid)
    if (!formFunctions) return []

    return Array.from(formFunctions.entries()).map(([id, { code, metadata }]) => ({
      id,
      code,
      metadata
    }))
  }

  /**
   * 获取指定表单和元素的可见性函数
   * @param {string} formUuid - 表单UUID
   * @param {string} elementName - 元素名称
   * @returns {Array} 该元素的可见性函数列表
   */
  getVisibilityFunctionsByElement(formUuid, elementName) {
    const formFunctions = this.visibilityFunctions.get(formUuid)
    if (!formFunctions) return []

    return Array.from(formFunctions.entries())
      .filter(([id, { metadata }]) => metadata.targetElement === elementName)
      .map(([id, { code, metadata }]) => ({
        id,
        code,
        metadata
      }))
  }

  /**
   * 获取指定表单的所有函数
   * @param {string} formUuid - 表单UUID
   * @returns {Object} 包含验证函数、同步函数和可见性函数的对象
   */
  getFormFunctions(formUuid) {
    return {
      validation: this.getValidationFunctions(formUuid),
      sync: this.getSyncFunctions(formUuid),
      visibility: this.getVisibilityFunctions(formUuid)
    }
  }

  /**
   * 清除指定表单的所有函数
   * @param {string} formUuid - 表单UUID
   */
  clearFormFunctions(formUuid) {
    this.validationFunctions.delete(formUuid)
    this.syncFunctions.delete(formUuid)
    this.visibilityFunctions.delete(formUuid)
    console.log(`[函数注册表] 已清除表单 ${formUuid} 的所有函数`)
  }

  /**
   * 清除所有函数（保持向后兼容）
   */
  clearAllFunctions() {
    this.validationFunctions.clear()
    this.syncFunctions.clear()
    this.visibilityFunctions.clear()
    console.log('[函数注册表] 已清除所有函数')
  }

  /**
   * 复制指定表单的所有函数到新表单
   * @param {string} oldFormUuid - 源表单UUID
   * @param {string} newFormUuid - 目标表单UUID
   */
  copyFormFunctions(oldFormUuid, newFormUuid) {
    // 复制验证函数
    const oldValidationFunctions = this.validationFunctions.get(oldFormUuid)
    if (oldValidationFunctions) {
      this.validationFunctions.set(newFormUuid, new Map(oldValidationFunctions))
    }

    // 复制同步函数
    const oldSyncFunctions = this.syncFunctions.get(oldFormUuid)
    if (oldSyncFunctions) {
      this.syncFunctions.set(newFormUuid, new Map(oldSyncFunctions))
    }

    // 复制可见性函数
    const oldVisibilityFunctions = this.visibilityFunctions.get(oldFormUuid)
    if (oldVisibilityFunctions) {
      this.visibilityFunctions.set(newFormUuid, new Map(oldVisibilityFunctions))
    }

    console.log(`[函数注册表] 已复制表单 ${oldFormUuid} 的所有函数到表单 ${newFormUuid}`)
  }
}

// 导出单例
export const functionRegistry = new FunctionRegistry()
